import { useMemo } from "react";
import { TrendUp } from "@phosphor-icons/react";
import { bestEstimated1RM } from "@/lib/oneRepMax";
import { OverviewCard, OverviewEmpty } from "./Card";

/**
 * Widget #4 — Migliori progressioni (peso e ripetizioni).
 *
 * Calcoliamo per ogni esercizio:
 *   best_set = MAX(weight_kg) lungo tutta la storia
 *   improvement = best_set - first_session_best
 * Mostriamo i top 5 in base al delta peso assoluto. In caso di parità (es.
 * tutti corpo libero) usiamo le rep totali della migliore sessione come
 * tie-breaker — copre anche progressioni "solo rep".
 */

function bestSet(sets) {
  let best = { weight: 0, reps: 0 };
  (sets || []).forEach((s) => {
    const w = Number(s.weight_kg) || 0;
    const r = Number(s.reps) || 0;
    if (w > best.weight || (w === best.weight && r > best.reps)) {
      best = { weight: w, reps: r };
    }
  });
  return best;
}

export function TopProgressionsWidget({ logs }) {
  const top = useMemo(() => {
    if (!logs?.length) return [];
    // Group logs by exercise_name, then sort by date ascending.
    const byExercise = new Map();
    logs.forEach((l) => {
      if (!byExercise.has(l.exercise_name)) byExercise.set(l.exercise_name, []);
      byExercise.get(l.exercise_name).push(l);
    });

    const rows = [];
    byExercise.forEach((entries, name) => {
      if (entries.length < 2) return; // need at least two sessions to show a delta
      const sorted = [...entries].sort((a, b) => (a.date > b.date ? 1 : -1));
      const first = bestSet(sorted[0].sets);
      const firstE1RM = bestEstimated1RM(sorted[0].sets);
      let best = first;
      let bestE1RM = firstE1RM;
      let bestDate = sorted[0].date;
      sorted.forEach((s) => {
        const b = bestSet(s.sets);
        const e1 = bestEstimated1RM(s.sets);
        if (e1 > bestE1RM) bestE1RM = e1;
        if (b.weight > best.weight || (b.weight === best.weight && b.reps > best.reps)) {
          best = b;
          bestDate = s.date;
        }
      });
      const dWeight = +(best.weight - first.weight).toFixed(1);
      const dReps = best.reps - first.reps;
      const dE1RM = +(bestE1RM - firstE1RM).toFixed(1);
      if (dWeight <= 0 && dReps <= 0 && dE1RM <= 0) return; // no improvement
      rows.push({
        name,
        from: first,
        to: best,
        fromE1RM: +firstE1RM.toFixed(1),
        toE1RM: +bestE1RM.toFixed(1),
        dWeight,
        dReps,
        dE1RM,
        bestDate,
        sessions: sorted.length,
      });
    });

    // Sort by estimated 1RM improvement — most meaningful indicator of
    // strength gain (combines load and reps into a single number).
    return rows
      .sort((a, b) => b.dE1RM - a.dE1RM || b.dWeight - a.dWeight || b.dReps - a.dReps)
      .slice(0, 5);
  }, [logs]);

  return (
    <OverviewCard
      title="Migliori progressioni"
      icon={TrendUp}
      iconColor="text-[#34C759]"
      testId="overview-top-progressions"
    >
      {top.length === 0 ? (
        <OverviewEmpty message="Servono almeno due sessioni dello stesso esercizio per misurare la progressione." />
      ) : (
        <ul className="space-y-2.5">
          {top.map((row) => (
            <li
              key={row.name}
              className="flex items-center justify-between gap-3 p-2.5 rounded-lg bg-[#171717] border border-[#1F1F1F]"
              data-testid={`overview-progression-${row.name}`}
            >
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-white truncate">{row.name}</p>
                <p className="text-[11px] text-zinc-500">
                  {row.from.weight}kg×{row.from.reps} → {row.to.weight}kg×{row.to.reps}
                </p>
                <p className="text-[10px] text-zinc-600 mt-0.5">
                  1RM teorico: {row.fromE1RM} → <span className="text-zinc-400">{row.toE1RM} kg</span>
                </p>
              </div>
              <div className="flex flex-col items-end text-xs whitespace-nowrap">
                {row.dE1RM > 0 && (
                  <span className="text-[#FF9F0A] font-bold">+{row.dE1RM} 1RM</span>
                )}
                {row.dWeight > 0 && (
                  <span className="text-[#34C759] font-semibold text-[11px]">+{row.dWeight} kg</span>
                )}
                {row.dReps > 0 && (
                  <span className="text-[#D4FF00] text-[11px]">+{row.dReps} rep</span>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </OverviewCard>
  );
}
